import { useEffect, useState, useCallback } from "react";
import {
    Box, Button, Typography, Paper, Stack, Chip, IconButton,
    Tooltip, Avatar, Divider
} from "@mui/material";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupIcon from '@mui/icons-material/Group';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import ModalKanbanModulo from "./ModalKanbanModulo";

export type KanbanModuloUsuario = {
    id_usuario: number;
    nome_usuario: string;
};

export type KanbanModulo = {
    id_kanban_modulo: number;
    id_escritorio_kanban: number;
    nome_modulo: string;
    cor_header: string;
    cor_corpo: string;
    ordem_modulo: number;
    kanban_modulo_ativo: number;
    created_at: string;
    updated_at: string;
    usuarios: KanbanModuloUsuario[];
};

export default function TabKanbanModulos() {
    const [openLoad, setOpenLoad] = useState(false);
    const [modulos, setModulos] = useState<KanbanModulo[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [editando, setEditando] = useState<KanbanModulo | null>(null);

    async function loadModulos() {
        setOpenLoad(true);
        try {
            const res = await api.get('/kanbanmodulos');
            if (res.data && !res.data.error) setModulos(res.data);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar módulos' });
        }
        setOpenLoad(false);
    }

    async function salvarOrdem(novaLista: KanbanModulo[]) {
        const ordem = novaLista.map((m, i) => ({
            id_kanban_modulo: m.id_kanban_modulo,
            ordem_modulo: i,
        }));
        try {
            await api.post('/reorderkanbanmodulos', { ordem });
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao salvar ordem' });
            loadModulos();
        }
    }

    const handleDragEnd = useCallback((result: DropResult) => {
        const { destination, source } = result;
        if (!destination) return;
        if (destination.index === source.index) return;

        setModulos(prev => {
            const lista = [...prev];
            const [moved] = lista.splice(source.index, 1);
            lista.splice(destination.index, 0, moved);
            salvarOrdem(lista);
            return lista;
        });
    }, []);

    async function handleDeletar(modulo: KanbanModulo) {
        AlertConfirm({ title: `Deseja realmente deletar o módulo "${modulo.nome_modulo}"?` }).then(async res => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const r = await api.delete(`/deletekanbanmodulo/${modulo.id_kanban_modulo}`);
                    if (r.data.success) {
                        AlertInfo({ icon: 'success', title: 'Módulo deletado!' });
                        loadModulos();
                    } else {
                        AlertInfo({ icon: 'error', title: r.data.message });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexão' });
                }
                setOpenLoad(false);
            }
        });
    }

    function handleEditar(modulo: KanbanModulo) {
        setEditando(modulo);
        setOpenModal(true);
    }

    function handleNovo() {
        setEditando(null);
        setOpenModal(true);
    }

    useEffect(() => { loadModulos(); }, []);

    return (
        <>
            <SimpleBackdrop open={openLoad} />

            <ModalKanbanModulo
                open={openModal}
                onClose={() => { setOpenModal(false); setEditando(null); }}
                onSaved={loadModulos}
                modulo={editando}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Módulos Kanban</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Arraste os módulos para definir a ordem de exibição
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon />}
                    onClick={handleNovo}
                    sx={{ textTransform: 'none' }}
                >
                    Novo Módulo
                </Button>
            </Stack>

            {modulos.length === 0 && !openLoad && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <ViewKanbanIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                        Nenhum módulo kanban cadastrado
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        Crie seu primeiro módulo clicando no botão acima
                    </Typography>
                </Box>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="kanban-modulos">
                    {(provided) => (
                        <Stack spacing={1.5} ref={provided.innerRef} {...provided.droppableProps}>
                            {modulos.map((modulo, index) => (
                                <Draggable
                                    key={modulo.id_kanban_modulo}
                                    draggableId={String(modulo.id_kanban_modulo)}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <Paper
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            variant="outlined"
                                            elevation={snapshot.isDragging ? 8 : 0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: snapshot.isDragging ? '#f5f5f5' : '#fff',
                                                transition: snapshot.isDragging ? 'none' : 'box-shadow 0.2s',
                                                '&:hover': { boxShadow: 2 },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                {/* Drag handle */}
                                                <Box
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'grab',
                                                        color: 'text.disabled',
                                                        '&:hover': { color: 'text.secondary' },
                                                        '&:active': { cursor: 'grabbing' },
                                                    }}
                                                >
                                                    <DragIndicatorIcon sx={{ fontSize: 22 }} />
                                                </Box>

                                                {/* Icone + Nome */}
                                                <ViewKanbanIcon color="primary" sx={{ fontSize: 22 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                                        {modulo.nome_modulo}
                                                    </Typography>

                                                    {/* Participantes inline */}
                                                    <Stack direction="row" alignItems="center" spacing={0.5} mt={0.3}>
                                                        <GroupIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                        {modulo.usuarios.length === 0 ? (
                                                            <Typography variant="caption" color="text.disabled">
                                                                Sem participantes
                                                            </Typography>
                                                        ) : (
                                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                                {modulo.usuarios.map(u => (
                                                                    <Chip
                                                                        key={u.id_usuario}
                                                                        size="small"
                                                                        avatar={
                                                                            <Avatar sx={{ width: 18, height: 18, fontSize: 9 }}>
                                                                                {u.nome_usuario?.charAt(0)}
                                                                            </Avatar>
                                                                        }
                                                                        label={u.nome_usuario}
                                                                        sx={{ fontSize: 11, height: 22 }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                </Box>

                                                {/* Acoes */}
                                                <Stack direction="row" spacing={0.3}>
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" color="primary" onClick={() => handleEditar(modulo)}>
                                                            <EditIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Deletar">
                                                        <IconButton size="small" color="error" onClick={() => handleDeletar(modulo)}>
                                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Stack>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
}
